import {useUpdateBenchMutation} from 'components/customer/BTM/store/btmApi';
import {LegacyEdgeInterface} from 'components/customer/Materials/helper/edgeHelper';
import {LegacyMaterialInterface} from 'components/customer/Materials/helper/materialHelper';
import {
    invalidateProductAndCost,
    useUpdateProductQuantityMutation,
} from 'components/customer/Product/store/productApi';
import {useNotificationContext} from 'contexts/NotificationContext';
import React, {useCallback, useEffect, useState} from 'react';
import {genericMessageHandler} from 'shared/helpers';
import {useAppDispatch} from 'store/customer';

export type PartialProduct = {
    id: number;
    dimensions: string;
    height?: number;
    width?: number;
    depth?: number;
    typeImage: string;
    typeName: string;
    productNumber: number;
    note: string;
    cabinet: {
        description: string;
    };
    extMaterial: LegacyMaterialInterface;
    carcMaterial: LegacyMaterialInterface;
    extEdge: LegacyEdgeInterface;
    carcEdge: LegacyEdgeInterface;
    comment: string;
    carcCustomColour: string;
    changedImage: string;
    extCustomColour: string;
    extHorGrain: number;
    carcHorGrain: number;
    includeAssembly: number;
    adjustableLegs: number;
    excludeHardware: number;
};

export enum ProductType {
    PRODUCT,
    BTM,
}

export const useProductQuantity = (
    value: number,
    productDetails: Partial<PartialProduct>,
    type = ProductType.PRODUCT
) => {
    const dispatch = useAppDispatch();
    const [quantity, setQuantity] = useState(value);
    const [saving, setSaving] = useState(false);
    const {notify} = useNotificationContext();
    const [updateProductQuantity] = useUpdateProductQuantityMutation();
    const [updateBTMQuantity] = useUpdateBenchMutation();

    const updateQuantity = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            event.stopPropagation();
            if (event.target.value == '') {
                setQuantity(undefined);
            } else {
                let value = parseInt(event.target.value);

                if (value <= 0) value = 1;

                setQuantity(value);
            }
        },
        [setQuantity]
    );

    const decreaseQuantity = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            quantity >= 2 && setQuantity(quantity - 1);
        },
        [quantity]
    );

    const increaseQuantity = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            setQuantity(quantity + 1);
        },
        [quantity]
    );

    const updateProduct = useCallback(
        async (event: React.MouseEvent) => {
            event.stopPropagation();
            if (!saving) {
                setSaving(true);
                try {
                    if (type === ProductType.PRODUCT) {
                        await updateProductQuantity({
                            cabinetId: productDetails.id,
                            quantity,
                        }).unwrap();

                        dispatch(invalidateProductAndCost());

                        genericMessageHandler(
                            notify,
                            {message: 'Product quantity updated'},
                            'success'
                        );
                    } else if (type === ProductType.BTM) {
                        await updateBTMQuantity({
                            bench: {quantity},
                            id: productDetails.id,
                        }).unwrap();
                    }

                    setSaving(false);
                } catch (e) {
                    setQuantity(value);
                    const error = e as {data?: {detail?: string}};
                    const message =
                        error?.data?.detail ??
                        'Product quantity could not be updated. Please try again later.';

                    genericMessageHandler(notify, {message});
                    setSaving(false);
                }
            }
        },
        [quantity, saving, type, value]
    );

    useEffect(() => {
        if (value != quantity) setQuantity(value);
    }, [value]);

    return {
        quantity,
        decreaseQuantity,
        increaseQuantity,
        updateQuantity,
        updateProduct,
        saving,
    };
};
